import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Spinner } from 'admin-components/components';
import { userShape, paymentsShape } from 'constants/propTypes';
import { codes } from 'constants/payment';
import { useMutation } from '@apollo/client';
import { REDEEM_VOUCHER, ROLLBACK_VOUCHER_REDEMPTION } from 'store/mutations';

import SplitPayment from '../../SplitPayment';
import { mapVoucherCurrencyToMarket, mapVoyagerToEvoucherChannels } from '../../Payments.utils';
import { handlePaymentSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import VoucherForm from './VoucherForm';
import VoucherDetails from './VoucherDetails';

const VoucherPayment = ({
  totalPriceToBePaid,
  currentAmount,
  isOnHold,
  payAndConfirm,
  takeDeposit,
  addPayment,
  reference,
  currency,
  method,
  loading,
  user,
  payments,
  isPutOnHoldWithDeposit
}) => {
  const channel = mapVoyagerToEvoucherChannels(user.salesChannel);
  const [voucherDetails, setVoucherDetails] = useState(null);
  const [redeemVoucherError, setRedeemVoucherError] = useState(null);
  const [redeemVoucher, { loading: isRedeemVoucherLoading }] = useMutation(REDEEM_VOUCHER, {
    onError: setRedeemVoucherError
  });

  const hasOnHoldNonVoucherPayments = isOnHold && !!payments.find((p) => p.method !== codes.EVOUCHER && p.isSuccessful);

  const voucherCode = voucherDetails?.voucher?.reference;

  useEffect(() => {
    setRedeemVoucherError(null);
  }, [voucherCode]);

  const [rollbackVoucherRedemption, { loading: isRollbackVoucherRedemptionLoading, error: rollbackVoucherRedemptionError }] =
    useMutation(ROLLBACK_VOUCHER_REDEMPTION);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountDue = parseFloat(currentAmount);
    const redeemAmount = amountDue > voucherDetails.voucher.balance ? voucherDetails.voucher.balance : amountDue;

    try {
      const {
        data: { redeemVoucher: { amountRedeemed, datacashReference, voucher } = {} }
      } = await redeemVoucher({
        variables: {
          reference: voucherCode,
          market: mapVoucherCurrencyToMarket[currency],
          channel,
          bookingReference: reference,
          amount: redeemAmount,
          cid: `VOY-EvoucherRedeem-payment-gateway-${reference}`,
          isDeposit: isPutOnHoldWithDeposit ? redeemAmount === totalPriceToBePaid : isPutOnHoldWithDeposit
        }
      });

      setVoucherDetails(null);
      try {
        return await handlePaymentSubmit({
          cashTendered: amountRedeemed,
          totalPriceToBePaid,
          payAndConfirm,
          takeDeposit,
          addPayment,
          isOnHold,
          bookingReference: reference,
          currency,
          method,
          reference: datacashReference,
          externalReference: voucher.alias
        });
      } catch (err) {
        const voucherPayments = payments
          .filter((payment) => payment.method === codes.VOUCHER)
          .map((payment) => ({ reference: payment.reference }))
          .concat({ reference: voucherCode });

        return Promise.all(
          voucherPayments.map((payment) =>
            rollbackVoucherRedemption({
              variables: {
                voucherCodeLastChars: payment.reference,
                market: mapVoucherCurrencyToMarket[currency],
                bookingReference: reference
              }
            })
          )
        );
      }
    } catch (err) {
      return err;
    }
  };

  if (isRedeemVoucherLoading || isRollbackVoucherRedemptionLoading) {
    return <Spinner />;
  }

  if (hasOnHoldNonVoucherPayments) {
    return (
      <Errors>
        <FormattedMessage id="payments.splitPaymentsMixWithVouchers" />
      </Errors>
    );
  }

  return (
    <SplitPayment
      title={<FormattedMessage id="payments.AmountToPayByEvoucher" />}
      subTitle={<FormattedMessage id="payments.canBeSplit" />}
      label={<FormattedMessage id="payments.recordPayment" />}
      loading={loading}
      onSubmit={handleSubmit}
      hideSubmit={voucherDetails === null}
      disabled={hasOnHoldNonVoucherPayments}
    >
      {!voucherDetails && (
        <VoucherForm
          bookingReference={reference}
          onSubmit={setVoucherDetails}
          resetVoucher={() => setVoucherDetails(null)}
          isDepositPayment={false}
        />
      )}
      {voucherDetails && (
        <>
          <VoucherDetails
            currency={currency}
            amount={voucherDetails.voucher.balance}
            redeemAmount={Math.min(voucherDetails.voucher.balance, parseFloat(currentAmount))}
            voucherCode={voucherDetails.voucher.reference}
            resetVoucher={() => setVoucherDetails(null)}
          />
          <Errors>{redeemVoucherError && <div data-cy="redeem-error-message">{redeemVoucherError.message}</div>}</Errors>
          <Errors>{rollbackVoucherRedemptionError && <FormattedMessage id="payments.voucherRollbackRedemptionError" />}</Errors>
        </>
      )}
    </SplitPayment>
  );
};

VoucherPayment.propTypes = {
  currentAmount: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  isOnHold: PropTypes.bool,
  payAndConfirm: PropTypes.func,
  takeDeposit: PropTypes.func,
  addPayment: PropTypes.func,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  user: userShape,
  payments: paymentsShape,
  isPutOnHoldWithDeposit: PropTypes.bool
};

export default VoucherPayment;
